import clsx from 'clsx';
import Cookies from 'js-cookie';
import React, { useCallback, useState } from 'react';

import Button from '@components/Button';
import BylineWithHeadshot from '@components/BylineWithHeadshot';
import ClientRenderedContent from '@components/ClientRenderedContent';
import IconWave from '@components/Images/IconWave';
import { useAppContext } from '@contexts/AppContext';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { Author } from '@interfaces/authors';
import { upgrade_subscription_path } from '@routes';
import { ClickTarget, click } from '@tracking';

import ArticleCommentJumpLink from './ArticleCommentJumpLink';
import AudioListenOnboardingTooltip, {
  AUDIO_LISTEN_ONBOARDING_COOKIE,
} from './AudioListenOnboardingTooltip';
import ShareArticleModal from './ShareArticleModal';

interface DetailsProps {
  authors: Author[];
  commentsCount?: number;
  commenters?: string[];
  publishedAt: string;
  articleId: number;
  canShareArticle: boolean;
  audioActive: boolean;
  setAudioActive: (audioActive: boolean) => void;
}

const STICKY_NAV_PLUS_SPACE = 100;

const ArticleDetails = ({
  authors,
  commentsCount = 0,
  commenters = [],
  publishedAt,
  articleId,
  canShareArticle,
  audioActive,
  setAudioActive,
}: DetailsProps) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showListenOnboarding, setShowListenOnboarding] = useState(false);
  const { isWebview } = useAppContext();
  const { trackEvent } = useEventTracking();
  const { currentUser, isLoadingCurrentUser } = useCurrentUser();

  const dismissListenOnboarding = useCallback(() => {
    if (currentUser?.subscription?.is_monthly) {
      const expires = new Date();
      expires.setMonth(expires.getMonth() + 6);
      Cookies.set(AUDIO_LISTEN_ONBOARDING_COOKIE, 'true', { expires });
    }
    setShowListenOnboarding(false);
  }, [currentUser?.subscription?.is_monthly]);

  const handleShareClick = () => {
    trackEvent(click({ clickTarget: ClickTarget.ShareButton }));
    setIsShareModalOpen(true);
  };

  const handleCommentJumpClick = useCallback(() => {
    const commentsSection = document.getElementById('comments-section');
    if (!commentsSection) return;
    const scrollTopDistance = commentsSection.getBoundingClientRect().top;
    window?.scrollBy({
      behavior: 'smooth',
      left: 0,
      top: scrollTopDistance - STICKY_NAV_PLUS_SPACE,
    });
  }, []);

  const handleAudioButtonClick = useCallback(() => {
    trackEvent(click({ clickTarget: ClickTarget.AudioButton }));

    if (!Cookies.get(AUDIO_LISTEN_ONBOARDING_COOKIE) && !currentUser?.subscription?.is_monthly) {
      setShowListenOnboarding(true);
      const expires = new Date();
      expires.setMonth(expires.getMonth() + 6);
      Cookies.set(AUDIO_LISTEN_ONBOARDING_COOKIE, 'true', { expires });
      setAudioActive(!audioActive);
    } else if (currentUser?.subscription?.is_monthly) {
      setShowListenOnboarding(true);
    } else {
      setAudioActive(!audioActive);
    }
  }, [audioActive, setAudioActive, trackEvent, currentUser?.subscription?.is_monthly]);

  if (isLoadingCurrentUser) return null;

  return (
    <>
      <div className="flex w-full flex-col gap-y-10 border-0 border-b border-solid border-grayscale-gray pb-24 sm:flex-row sm:items-center sm:gap-y-0">
        {authors.length > 0 && (
          <BylineWithHeadshot
            authors={authors}
            authorClassName="author-name-body"
            className="max-w-sm shrink-0 self-start sm:self-auto"
          />
        )}
        <div className="flex shrink-0 items-end justify-end gap-10 self-end sm:ml-auto sm:justify-start sm:self-auto">
          {!isWebview && (
            <span className="relative inline-flex print:hidden">
              {showListenOnboarding && (
                <AudioListenOnboardingTooltip
                  isMonthlySubscriber={currentUser?.subscription?.is_monthly}
                  setShowListenOnboarding={setShowListenOnboarding}
                />
              )}
              <Button
                as="button"
                className={clsx(
                  'audio-button group flex items-center gap-12 rounded-20 border bg-white px-16 py-8 font-suisse-intl-mono text-button-xs text-grayscale-gray outline-none hover:bg-primary-magenta',
                  {
                    '!bg-primary-magenta !text-white': audioActive,
                  }
                )}
                onClick={handleAudioButtonClick}
              >
                <IconWave
                  className={clsx('text-primary-magenta group-hover:text-white', {
                    '!text-white': audioActive,
                  })}
                />
                <span className="group-hover:text-white">Listen</span>
              </Button>
            </span>
          )}
          {canShareArticle && !isWebview && (
            <Button
              as="button"
              className="share-button flex items-center rounded-20 border-0 bg-grayscale-gray px-16 py-8 font-suisse-intl-mono text-button-xs text-white outline-none print:hidden"
              onClick={handleShareClick}
            >
              Share
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-end justify-end md:justify-between">
        <ClientRenderedContent>
          <time
            className="mb-0 mt-8 block font-suisse-intl-mono text-overline-md text-grayscale-gray lg:text-overline-sm xl:text-overline-md"
            dateTime={publishedAt}
          >
            {publishedAt}
          </time>
        </ClientRenderedContent>
        {commentsCount > 0 && (
          <ArticleCommentJumpLink
            commentsCount={commentsCount}
            commenters={commenters}
            onClick={handleCommentJumpClick}
          />
        )}
      </div>
      {canShareArticle && !isWebview && (
        <ShareArticleModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          articleId={articleId}
        />
      )}
    </>
  );
};

export default ArticleDetails;
